import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, ChevronRight, CircleAlert, CircleCheck, Search, X } from 'lucide-react';
import useAuthStore from '../../../core/stores/authStore';
import { useAdCampaignJoin, useAdCampaigns } from '../../hooks/marketing/useAdCampaigns';
import { splitCampaigns, todayISO } from './adCampaignMapping';
import styles from './TossMarketing.module.css';

// 노출 대상·노출/사용 수·종료 캠페인·사용내역은 정산 서버 작업 대기 → '-' 또는 빈 목록. FAQ 는 입력란이 없어 제거 (백엔드 회신 10/22)
const CHANNEL = '팜페이앱'; // 알림톡은 나가지 않음 (백엔드 10/22)
const completed = [];

function Toggle({ on, onClick, label, disabled }) {
  return <button type="button" aria-label={label} aria-pressed={on} disabled={disabled} className={`${styles.toggle} ${on ? styles.on : ''}`} onClick={onClick}><i /></button>;
}

function CampaignCard({ campaign: c, mode, selected, decision, busy, onClick, onToggle }) {
  const review = mode === 'review';
  const done = mode === 'done';
  const cls = [styles.campaignCard, styles[mode], selected && styles.selected, c.notHandled && review && !decision && styles.dim].filter(Boolean).join(' ');
  return <div role="button" tabIndex={0} className={cls} onClick={onClick} onKeyDown={e => e.key === 'Enter' && onClick()}>
    <div className={styles.cardHead}>
      <div className={styles.cardHeadText}>
        <span className={styles.brand}>{c.company}{c.isNew && <em>NEW</em>}</span>
        <strong>{c.title}</strong>
        <small>{c.desc}</small>
      </div>
      {review && <div className={styles.decision}>
        <span className={decision ? '' : styles.off}>{decision ? '참여 예정' : '미참여'}</span>
        <Toggle on={decision} disabled={busy} label="캠페인 참여" onClick={e => { e.stopPropagation(); onToggle(); }} />
      </div>}
    </div>
    <div className={styles.metrics}>
      <div>
        <label>기간</label>
        <div className={styles.duration}>
          <b>{c.period}</b>
          {c.deadline && <em className={styles.deadline}>{c.deadline}</em>}
          {review && <em className={styles.dday}>{c.dday} 시작 예정</em>}
        </div>
        {mode === 'active' ? <>
          <div className={styles.progress}><i className={c.status === 'empty' ? styles.redBar : ''} style={{ width: `${c.progress}%` }} /></div>
          <div className={styles.progressMeta}><span>{c.elapsed}</span><span>{c.progress}%</span></div>
        </> : <small className={styles.muted}>{c.duration}</small>}
      </div>
      {done ? <>
        <div><label>노출</label><b>{c.exposure}</b></div>
        <div><label>사용</label><b>{c.used}</b></div>
        <div><label>사용률</label><b>{c.rate}</b><small className={styles.dark}>{c.rateNote}</small></div>
        <div><label>매출 기여</label><b className={styles.sub1}>{c.revenue}</b></div>
      </> : <>
        <div><label>{review ? '예상 노출' : '내 단골 노출'}</label><b>{c.exposure}</b><small>{c.target}</small></div>
        <div><label>{review ? '예상 사용' : '현재 사용'}</label><b>{c.used}</b><small>{c.rate || '​'}</small></div>
        {c.notHandled
          ? <div><b className={styles.orange}>미취급</b><b className={styles.sub1}>—</b></div>
          : <div><label>재고</label><b className={c.status === 'empty' || c.stockDanger ? styles.danger : review ? styles.sub1 : ''}>{c.stock}</b>{c.turn && <small>{c.turn}</small>}</div>}
      </>}
    </div>
  </div>;
}

function Head({ c, tag }) {
  return <div className={styles.detailHead}><span>{c.company}</span><h2>{c.title}</h2><p>{c.desc}</p><div><em>{tag}</em></div></div>;
}

function Block({ title, className, children }) {
  return <section className={className}><h3>{title}</h3>{children}</section>;
}

function Rows({ rows }) {
  return <>{rows.map(([k, v, cls]) => <div key={k} className={styles.row}><span>{k}</span><b className={v === '-' ? '' : cls}>{v}</b></div>)}</>;
}

function Stats({ items, className }) {
  return <div className={`${styles.stats} ${className || ''}`}>{items.map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}</div>;
}

function Notice({ tone, icon, title, desc }) {
  return <div className={`${styles.notice} ${styles[tone]}`}>{icon}<div><b>{title}</b><small>{desc}</small></div></div>;
}

function InfoBlock({ c }) {
  return <Block title="캠페인 정보"><div className={styles.box}><Rows rows={[['발행', c.company], ['진행 기간', c.info.period], ['할인 방식', c.info.discount], ['노출 채널', CHANNEL], ['노출 대상', c.info.target]]} /></div></Block>;
}

function DetailPanel({ c, mode, decision, busy, onParticipate }) {
  if (mode === 'done') {
    const r = c.result;
    return <aside className={styles.rightColumn}><div className={`${styles.detailCard} ${styles.r12}`}>
      <Head c={c} tag={c.tag} />
      <Block title="최종 결과 · 우리 약국" className={styles.blueTitle}>
        <Stats items={[['노출', r.exposure], ['사용', r.used]]} />
        <Stats items={[['사용률', r.rate], ['매출기여', r.revenue]]} />
      </Block>
      <Block title="전국 비교 (사용률)">
        <div className={`${styles.box} ${styles.compare}`}>
          <div><span>전국 평균</span><i><s style={{ width: r.nationalFill }} /></i><b>{r.national}</b></div>
          <div className={styles.mine}><span>우리 약국</span><i><s style={{ width: r.mineFill }} /></i><b>{r.rate}</b></div>
        </div>
      </Block>
      <Block title="재고 변화 (캠페인 기간)">
        <div className={styles.box}><b className={styles.boxTitle}>{c.product}</b><hr /><Rows rows={[['캠페인 전 재고', r.before], ['추가 발주', r.ordered, styles.blue], ['캠페인 후 재고', r.after]]} /></div>
      </Block>
      <InfoBlock c={c} />
    </div></aside>;
  }

  if (mode === 'review' && !decision) {
    return <aside className={styles.rightColumn}><div className={`${styles.detailCard} ${styles.r12}`}>
      <Head c={c} tag={`미참여 상태 · ${c.dday} 시작 예정`} />
      <Block title="미참여 상태">
        <div className={styles.nonPart}>
          <p><CircleAlert size={16} fill="#f04351" color="#fff" />이 캠페인은 미참여 상태 · 환자에게 노출되지 않습니다</p>
          <div><div><b>참여로 변경</b><small>시작 전까지 결정 변경 가능</small></div><Toggle on={false} disabled={busy} label="참여로 변경" onClick={onParticipate} /></div>
        </div>
      </Block>
      <Block title="약사 응대 가이드 (참고)" className={styles.faded}><p className={styles.guide}>{c.guide}</p></Block>
      <Block title="대상 상품 · 내 약국 재고">
        <div className={styles.box}><b className={styles.boxTitle}>{c.product}</b><hr /><Rows rows={[['현재 재고', c.stockLine], ['미참여 사유', c.reason, styles.danger]]} /></div>
        <Notice tone="gray" icon={<X size={16} />} title="발주 불필요" desc="미참여 상태 · 참여로 변경 시 발주 검토" />
      </Block>
      <Block title="노출 · 사용 현황" className={styles.faded}><Stats items={[['노출', '—'], ['사용', '—']]} /></Block>
      <InfoBlock c={c} />
    </div></aside>;
  }

  const empty = c.status === 'empty';
  const review = mode === 'review';
  const productRows = empty
    ? [['현재 재고', c.stockLine, styles.danger], ['최근 입고', c.lastInbound]]
    : [['현재 재고', c.stockLine, c.notHandled ? styles.orange : styles.blue], ['판매 단가', c.price], ['이번 캠페인 사용 시', c.benefit === '-' ? c.benefit : `${c.benefit.replace(/ 증정$/, '')} 자동 적용`]];
  return <aside className={styles.rightColumn}><div className={`${styles.detailCard} ${mode === 'active' && !empty ? styles.p24 : ''}`}>
    <Head c={c} tag={review ? `참여 예정 · ${c.dday}` : c.tag} />
    <Block title="약사 응대 가이드"><p className={styles.guide}>{c.guide}</p></Block>
    <Block title="대상 상품 · 내 약국 재고">
      <div className={styles.box}><b className={styles.boxTitle}>{c.product}</b><hr /><Rows rows={productRows} /></div>
      {c.status && (empty
        ? <Notice tone="warning" icon={<CircleAlert size={16} fill="#f79103" color="#fff" />} title="재발주 권장" desc="진행 중 캠페인 · 환자 응대 가능하도록 POS에서 즉시 재발주" />
        : <Notice tone="ok" icon={<CircleCheck size={16} fill="#34c759" color="#fff" />} title="재고 충분" desc={`예상 사용 약 ${c.expectedUse} · 현재 재고로 충분히 응대 가능`} />)}
    </Block>
    <Block title="노출 · 사용 현황"><Stats className={review ? styles.pending : ''} items={review ? [['노출', '—'], ['사용', '—']] : [['노출', c.exposure], ['사용', c.used]]} /></Block>
    <InfoBlock c={c} />
  </div></aside>;
}

function Radio({ label, checked, onChange }) {
  return <label className={`${styles.radio} ${checked ? styles.checked : ''}`}><input type="radio" checked={checked} onChange={onChange} /><i />{label}</label>;
}

function UsageHistory() {
  const [query, setQuery] = useState('');
  const [range, setRange] = useState('이번달');
  const [status, setStatus] = useState('전체');
  const q = query.trim();
  const usageGroups = [];
  const today = todayISO();
  const groups = usageGroups
    .map(g => ({ ...g, rows: g.rows.filter(r => (status === '전체' || r[4] === status) && (!q || [g.brand, g.title, ...r].some(v => v.includes(q)))) }))
    .filter(g => g.rows.length);
  return <div className={styles.history}>
    <div className={styles.summaryCards}>{usageGroups.map(g => <div key={g.brand}>
      <div className={styles.kpiTitle}><span>{g.brand}</span><b>{g.title}</b></div>
      <div className={styles.kpiStats}><div><div className={styles.kpiUse}><span>사용</span><b>{g.count}</b></div><div className={styles.kpiBenefit}><span>총 혜택</span><b>{g.benefit}</b></div></div></div>
    </div>)}</div>
    <div className={styles.search}><Search size={20} /><input value={query} onChange={e => setQuery(e.target.value)} placeholder="캠페인명, 브랜드, 환자 검색" /><button type="button" aria-label="검색어 지우기" onClick={() => setQuery('')}><X size={16} /></button></div>
    <div className={styles.filters}>
      <div><span className={styles.filterLabel}>조회기간</span><div className={styles.filterBody}><span className={styles.dates}><b>{today.slice(0, 8)}01</b><small>~</small><b>{today}</b></span><CalendarDays size={18} className={styles.calendar} />{['어제', '지난주', '지난달', '이번달'].map(v => <Radio key={v} label={v} checked={range === v} onChange={() => setRange(v)} />)}</div></div>
      <div><span className={styles.filterLabel}>상태</span><div className={styles.filterBody}>{['전체', '사용', '취소'].map(v => <Radio key={v} label={v} checked={status === v} onChange={() => setStatus(v)} />)}</div></div>
    </div>
    <div className={styles.historyTable}>
      {groups.length === 0 && <div className={styles.empty}>사용 내역이 없습니다</div>}
      {groups.map(g => <div key={g.brand}>
        <div className={styles.groupTitle}><span><small>{g.brand}</small><b>{g.title}</b></span><span className={styles.groupTotal}><small>{g.rows.length}건 ·</small><b>{g.total}</b></span></div>
        <div className={`${styles.tr} ${styles.th}`}><span>사용 일시</span><span>환자</span><span>적용 상품</span><span>혜택</span><span>상태</span></div>
        {g.rows.map(r => { const cancel = r[4] === '취소'; return <div className={`${styles.tr} ${cancel ? styles.cancelRow : ''}`} key={r[0]}>{r.map((v, i) => <span key={i}>{v}</span>)}</div>; })}
      </div>)}
      <div className={styles.total}><b>합계</b><div><span><small>총 거래</small><b>-</b></span><span><small>환자 혜택 합계</small><strong>-</strong></span></div></div>
    </div>
  </div>;
}

function BrandFilter({ brands, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return <div className={styles.brandFilter} ref={ref}>
    <button type="button" className={styles.brandButton} onClick={() => setOpen(v => !v)}>{value || '전체 브랜드'} <img src="/images/marketing/icon-down.svg" width="18" height="18" alt="" /></button>
    {open && <ul className={styles.brandMenu}>{['', ...brands].map(b => <li key={b || 'all'}><button type="button" className={b === value ? styles.activeBrand : ''} onClick={() => { onChange(b); setOpen(false); }}>{b || '전체 브랜드'}</button></li>)}</ul>}
  </div>;
}

const EMPTY_TEXT = { active: '진행중인 캠페인이 없습니다', review: '검토할 캠페인이 없습니다', done: '종료된 캠페인이 없습니다' };

export default function TossMarketing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMode = searchParams.get('view');
  const mode = ['review', 'done', 'history'].includes(requestedMode) ? requestedMode : 'active';
  useEffect(() => { window.dispatchEvent(new CustomEvent('promotion-mode', { detail: mode })); }, [mode]);
  const brn = useAuthStore(state => state.pharmacy?.business_registration_number);
  const { data, isLoading } = useAdCampaigns(brn);
  const joinMutation = useAdCampaignJoin();
  const tabs = useMemo(() => splitCampaigns(data?.items, todayISO()), [data]);
  const source = mode === 'review' ? tabs.review : mode === 'done' ? completed : tabs.active;
  const [brand, setBrand] = useState('');
  const [selected, setSelected] = useState(null);
  useEffect(() => { setBrand(''); setSelected(null); }, [mode]);
  const brands = useMemo(() => [...new Set(source.map(c => c.company))], [source]);
  const list = brand ? source.filter(c => c.company === brand) : source;
  const current = list.find(c => c.id === selected) || list[0];
  const toggle = c => joinMutation.mutate({ id: c.id, join: !c.joined }, { onError: error => alert(error.message) });

  if (mode === 'history') return <UsageHistory />;
  return <div className={styles.page}>
    <div className={`${styles.leftColumn} ${styles[mode]}`}>
      {mode === 'active' && tabs.pendingReview > 0 && <div className={styles.hero}>
        <div><h2><span>검토 대기 캠페인</span><b>{tabs.pendingReview}건</b><span>있습니다</span></h2><p>참여 시 우리 약국 단골에게 쿠폰이 자동 발급됩니다. 미결정 시 자동 미참여됩니다.</p></div>
        <button type="button" onClick={() => setSearchParams({ view: 'review' })}>검토하러 가기 <ChevronRight size={20} /></button>
      </div>}
      {mode === 'review' && <div className={styles.reviewAlert}>
        <CircleAlert size={16} fill="#5081e9" color="#fff" />
        <div><b>아래 쿠폰들은 시작 예정입니다.</b><ul><li>참여 시 시작일에 우리 약국 단골 중 조건 매칭되는 환자에게 쿠폰이 자동 발급됩니다.</li><li>미참여시 환자에게 우리 약국이 사용 가능 약국으로 보이지 않습니다 (이미 발급된 쿠폰은 기간 내 사용 보장).</li></ul></div>
      </div>}
      <div className={styles.listHeader}><h1>총 {isLoading ? '-' : list.length}건</h1><BrandFilter brands={brands} value={brand} onChange={setBrand} /></div>
      {!isLoading && list.length === 0 && <div className={styles.empty}>{EMPTY_TEXT[mode]}</div>}
      <div className={styles.list}>{list.map(c => <CampaignCard key={c.id} campaign={c} mode={mode} selected={current?.id === c.id} decision={c.joined} busy={joinMutation.isPending} onClick={() => setSelected(c.id)} onToggle={() => toggle(c)} />)}</div>
    </div>
    {current && <DetailPanel c={current} mode={mode} decision={current.joined} busy={joinMutation.isPending} onParticipate={() => toggle(current)} />}
  </div>;
}
